/* eslint-disable react/prop-types */
import * as yup from "yup";
import { v4 as uuid } from "uuid";
import { useMemo } from "react";
import { TYPE_FIELD_LIST } from "src/helper/TypeFieldList";
import { MESSAGE } from "src/message";
import { USER_ROLE_TYPE, userType } from "src/constant";
import useAuthGlobal from "src/context/useAuthGlobal";

export default function useCustomerSchema({ item }) {
  const { userMe } = useAuthGlobal();

  const isAdminWarehouseOnly = userMe?.role?.code === USER_ROLE_TYPE.ADMIN_WAREHOUSE;

  const isCreate = item === undefined;

  const userFieldList = useMemo(
    () => [
      {
        id: uuid(),
        name: "type",
        label: "Loại nhân viên",
        type: "radio",
        grid: [12],
        typeField: TYPE_FIELD_LIST.RADIO,
        placeHolder: MESSAGE.ORIGIN_NAME,
        defaultValue: userType.staff,
        child: [
          {
            id: uuid(),
            name: userType.staff,
            label: "Nhân viên Kho/Kế toán",
            typeField: TYPE_FIELD_LIST.RADIO,
            type: "text",
            defaultValue: "",
            grid: [6],
            placeHolder: MESSAGE.ORIGIN_NAME,
          },
          {
            id: uuid(),
            name: userType.sales,
            label: "Nhân viên Kinh doanh",
            typeField: TYPE_FIELD_LIST.RADIO,
            type: "text",
            defaultValue: "",
            grid: [6],
            placeHolder: MESSAGE.ORIGIN_NAME,
          },
        ],
      },
      {
        id: uuid(),
        name: "warehouse",
        label: "Kho",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        type: "text",
        defaultValue: null,
        grid: [12],
        path: "/api/v1/warehouses",
        params: {
          limit: 10,
          skip: 0,
          sortField: "createdAt",
          sortOrder: "asc",
          searchField: "name",
        },
        placeHolder: "Nhà sản xuất",
        required: isCreate,
        disabled: isAdminWarehouseOnly,
      },
      {
        id: uuid(),
        name: "role",
        label: "Vai trò",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        type: "text",
        defaultValue: null,
        grid: [12],
        path: "/api/v1/roles",
        params: {
          limit: 10,
          skip: 0,
          sortField: "createdAt",
          sortOrder: "asc",
          searchField: "name",
          warehouseType: userMe?.currentWarehouse?.type,
        },
        placeHolder: "Vai trò",
        required: isCreate,
      },
      {
        id: uuid(),
        name: "username",
        label: "Tên đăng nhập",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: "Tên đăng nhập",
        required: true,
      },
      {
        id: uuid(),
        name: "password",
        label: `Mật khẩu ${!isCreate ? "mới" : ""}`,
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "password",
        defaultValue: "",
        grid: [12],
        placeHolder: `Mật khẩu ${!isCreate ? "mới" : ""}`,
        required: isCreate,
      },
      {
        id: uuid(),
        name: "repassword",
        label: `Nhập lại mật khẩu ${!isCreate ? "mới" : ""}`,
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "password",
        defaultValue: "",
        grid: [12],
        placeHolder: `Nhập lại mật khẩu ${!isCreate ? "mới" : ""}`,
        required: isCreate,
      },
      {
        id: uuid(),
        name: "fullName",
        label: "Tên đầy đủ",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: "Tên đầy đủ",
        required: true,
      },
      {
        id: uuid(),
        name: "phone",
        label: "Số điện thoại",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: "Số điện thoại",
        required: true,
      },
      {
        id: uuid(),
        name: "email",
        label: "Email",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: "Email",
        required: true,
      },
    ],
    [isAdminWarehouseOnly, isCreate, userMe?.currentWarehouse?.type],
  );

  const validationSchema = yup.object().shape({
    warehouse: yup
      .object({
        _id: isCreate ? yup.string().required("Vui lòng chọn kho") : yup.string().notRequired(),
      })
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("type", {
        is: (val) => isCreate && val !== userType.sales,
        then: (schema) => schema.required("Vui lòng chọn kho"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    role: yup
      .object({
        _id: isCreate ? yup.string().required("Vui lòng chọn vai trò") : yup.string().notRequired(),
      })
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("type", {
        is: (val) => isCreate && val !== userType.sales,
        then: (schema) => schema.required("Vui lòng chọn vai trò"),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    type: yup.string().oneOf([userType.staff, userType.sales], "Loại không hợp lệ").required("Loại người dùng là bắt buộc"),
    username: yup.string().required("Tên người dùng là bắt buộc").min(3, "Tên người dùng phải có ít nhất 3 ký tự"),
    password: yup.string().when([], {
      is: () => isCreate,
      then: (schema) => schema.required("Vui lòng nhập mật khẩu").min(6, "Mật khẩu phải có ít nhất 6 ký tự"),
      otherwise: (schema) => schema.notRequired(),
    }),
    repassword: yup.string().when([], {
      is: () => isCreate,
      then: (schema) =>
        schema
          .required("Vui lòng nhập lại mật khẩu")
          .min(6, "Mật khẩu phải có ít nhất 6 ký tự")
          .oneOf([yup.ref("password"), null], "Mật khẩu không khớp"),
      otherwise: (schema) => schema.notRequired(),
    }),
    fullName: yup.string().required("Họ và tên là bắt buộc"),
    phone: yup
      .string()
      .matches(/^[0-9]{10,11}$/, "Số điện thoại phải có 10-11 chữ số và chỉ bao gồm số")
      .required("Số điện thoại là bắt buộc"),
    email: yup.string().email("Email không hợp lệ").required("Email là bắt buộc"),
  });

  const userObject = {
    warehouse: null,
    role: null,
    type: userType.staff,
    username: "",
    password: "",
    repassword: "",
    fullName: "",
    phone: "",
    email: "",
  };

  return {
    userFieldList,
    validationSchema,
    userObject,
  };
}
